/**
 * iTop API Service
 * Handles all communication with the iTop REST API
 */

import {
  ITopCredentials,
  ITopOrganization,
  ITopPerson,
  ITopTeam,
  ITopService,
  ITopTicket,
  CreateTicketRequest,
  CreateTicketResponse,
  CacheEntry
} from '../models/itop.models';
import { CredentialService } from './credential.service';

// Cache durations in milliseconds
const CACHE_DURATION = {
  ORGANIZATIONS: 15 * 60 * 1000, // 15 minutes
  TEAMS: 15 * 60 * 1000,         // 15 minutes
  SERVICES: 15 * 60 * 1000,      // 15 minutes
  PERSONS: 5 * 60 * 1000         // 5 minutes
};

export class ITopApiService {
  private static cache: Map<string, CacheEntry<unknown>> = new Map();

  /**
   * Check if cached data is still valid
   */
  private static isCacheValid<T>(key: string, maxAge: number): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;

    const age = Date.now() - entry.timestamp;
    if (age > maxAge) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cache entry
   */
  private static setCache<T>(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Clear all cached data
   */
  static clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get credentials or throw error
   */
  private static getCredentialsOrThrow(): ITopCredentials {
    const credentials = CredentialService.get();
    if (!credentials) {
      throw new Error('iTop credentials not configured. Please go to Settings.');
    }
    return credentials;
  }

  /**
   * Make API request to iTop
   */
  private static async makeRequest<T>(
    operation: string,
    jsonData: Record<string, unknown>
  ): Promise<T> {
    const credentials = this.getCredentialsOrThrow();

    const formData = new FormData();
    formData.append('auth_user', credentials.username);
    formData.append('auth_pwd', credentials.password);
    formData.append('json_data', JSON.stringify({
      operation,
      ...jsonData
    }));

    const response = await fetch(credentials.url, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.code !== 0) {
      throw new Error(result.message || 'iTop API error');
    }

    return result;
  }

  /**
   * Parse iTop objects from API response
   */
  private static parseObjects<T>(
    objects: Record<string, { fields: Record<string, unknown> }> | null,
    mapper: (key: string, fields: Record<string, unknown>) => T
  ): T[] {
    if (!objects) return [];

    return Object.entries(objects).map(([key, obj]) => mapper(key, obj.fields));
  }

  /**
   * Test connection with provided credentials
   */
  static async testConnection(
    username: string,
    password: string,
    url: string
  ): Promise<boolean> {
    const formData = new FormData();
    formData.append('auth_user', username);
    formData.append('auth_pwd', password);
    formData.append('json_data', JSON.stringify({
      operation: 'core/get',
      class: 'Organization',
      key: 'SELECT Organization WHERE status = "active"',
      output_fields: 'name',
      limit: 1
    }));

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) return false;

      const result = await response.json();
      return result.code === 0;
    } catch {
      return false;
    }
  }

  /**
   * Get all active organizations
   */
  static async getOrganizations(): Promise<ITopOrganization[]> {
    const cacheKey = 'organizations';
    const cached = this.isCacheValid<ITopOrganization[]>(cacheKey, CACHE_DURATION.ORGANIZATIONS);
    if (cached) return cached;

    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'Organization',
      key: 'SELECT Organization WHERE status = "active"',
      output_fields: 'name, code, status'
    });

    const organizations = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      name: fields.name as string,
      code: fields.code as string,
      status: fields.status as string
    }));

    // Sort by name
    organizations.sort((a, b) => a.name.localeCompare(b.name));

    this.setCache(cacheKey, organizations);
    return organizations;
  }

  /**
   * Get persons for an organization
   */
  static async getPersonsForOrganization(orgId: number): Promise<ITopPerson[]> {
    const cacheKey = `persons_${orgId}`;
    const cached = this.isCacheValid<ITopPerson[]>(cacheKey, CACHE_DURATION.PERSONS);
    if (cached) return cached;

    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'Person',
      key: `SELECT Person WHERE org_id = ${orgId}`,
      output_fields: 'name, first_name, email, org_id, friendlyname'
    });

    const persons = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      name: fields.name as string,
      first_name: fields.first_name as string,
      email: fields.email as string,
      org_id: fields.org_id as number,
      friendlyname: fields.friendlyname as string
    }));

    // Sort by name
    persons.sort((a, b) => `${a.first_name} ${a.name}`.localeCompare(`${b.first_name} ${b.name}`));

    this.setCache(cacheKey, persons);
    return persons;
  }

  /**
   * Find person by email across all organizations
   */
  static async findPersonByEmail(email: string): Promise<ITopPerson | null> {
    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'Person',
      key: `SELECT Person WHERE email = '${email.replace(/'/g, "\\'")}'`,
      output_fields: 'name, first_name, email, org_id, org_name, friendlyname'
    });

    const persons = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      name: fields.name as string,
      first_name: fields.first_name as string,
      email: fields.email as string,
      org_id: fields.org_id as number,
      org_name: fields.org_name as string,
      friendlyname: fields.friendlyname as string
    }));

    return persons.length > 0 ? persons[0] : null;
  }

  /**
   * Get all teams
   */
  static async getTeams(): Promise<ITopTeam[]> {
    const cacheKey = 'teams';
    const cached = this.isCacheValid<ITopTeam[]>(cacheKey, CACHE_DURATION.TEAMS);
    if (cached) return cached;

    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'Team',
      key: 'SELECT Team',
      output_fields: 'name, org_id'
    });

    const teams = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      name: fields.name as string,
      org_id: fields.org_id as number
    }));

    teams.sort((a, b) => a.name.localeCompare(b.name));

    this.setCache(cacheKey, teams);
    return teams;
  }

  /**
   * Get agents (persons) for a team
   */
  static async getAgentsForTeam(teamId: number): Promise<ITopPerson[]> {
    const cacheKey = `agents_${teamId}`;
    const cached = this.isCacheValid<ITopPerson[]>(cacheKey, CACHE_DURATION.PERSONS);
    if (cached) return cached;

    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'Person',
      key: `SELECT Person AS p JOIN lnkPersonToTeam AS l ON l.person_id = p.id WHERE l.team_id = ${teamId}`,
      output_fields: 'name, first_name, email, friendlyname'
    });

    const agents = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      name: fields.name as string,
      first_name: fields.first_name as string,
      email: fields.email as string,
      org_id: 0,
      friendlyname: fields.friendlyname as string
    }));

    agents.sort((a, b) => `${a.first_name} ${a.name}`.localeCompare(`${b.first_name} ${b.name}`));

    this.setCache(cacheKey, agents);
    return agents;
  }

  /**
   * Get all services
   */
  static async getServices(): Promise<ITopService[]> {
    const cacheKey = 'services';
    const cached = this.isCacheValid<ITopService[]>(cacheKey, CACHE_DURATION.SERVICES);
    if (cached) return cached;

    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'Service',
      key: 'SELECT Service',
      output_fields: 'name, org_id'
    });

    const services = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      name: fields.name as string,
      org_id: fields.org_id as number
    }));

    services.sort((a, b) => a.name.localeCompare(b.name));

    this.setCache(cacheKey, services);
    return services;
  }

  /**
   * Get tickets for an organization (excludes closed/rejected)
   */
  static async getTicketsForOrganization(orgId: number): Promise<ITopTicket[]> {
    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'UserRequest',
      key: `SELECT UserRequest WHERE org_id = ${orgId} AND status NOT IN ('closed', 'rejected')`,
      output_fields: 'ref, title, description, org_id, org_name, caller_id, caller_name, team_id, team_name, agent_id, agent_name, status, priority'
    });

    const tickets = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      ref: fields.ref as string,
      title: fields.title as string,
      description: fields.description as string,
      org_id: fields.org_id as number,
      org_name: fields.org_name as string,
      caller_id: fields.caller_id as number,
      caller_name: fields.caller_name as string,
      team_id: fields.team_id as number,
      team_name: fields.team_name as string,
      agent_id: fields.agent_id as number,
      agent_name: fields.agent_name as string,
      status: fields.status as string,
      priority: fields.priority as string
    }));

    // Sort by ref descending (newest first)
    tickets.sort((a, b) => b.ref.localeCompare(a.ref));

    return tickets;
  }

  /**
   * Get a single ticket by ID
   */
  static async getTicketById(ticketId: number): Promise<ITopTicket | null> {
    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/get', {
      class: 'UserRequest',
      key: ticketId,
      output_fields: 'ref, title, description, org_id, org_name, caller_id, caller_name, team_id, team_name, agent_id, agent_name, status, priority'
    });

    const tickets = this.parseObjects(result.objects, (key, fields) => ({
      id: parseInt(key.split('::')[1]),
      ref: fields.ref as string,
      title: fields.title as string,
      description: fields.description as string,
      org_id: fields.org_id as number,
      org_name: fields.org_name as string,
      caller_id: fields.caller_id as number,
      caller_name: fields.caller_name as string,
      team_id: fields.team_id as number,
      team_name: fields.team_name as string,
      agent_id: fields.agent_id as number,
      agent_name: fields.agent_name as string,
      status: fields.status as string,
      priority: fields.priority as string
    }));

    return tickets.length > 0 ? tickets[0] : null;
  }

  /**
   * Create a new ticket (UserRequest)
   * Uses a two-step process: create the ticket, then assign via ev_assign stimulus
   */
  static async createTicket(request: CreateTicketRequest): Promise<CreateTicketResponse> {
    // Step 1: Create ticket with basic fields (no team/agent)
    const fields: Record<string, unknown> = {
      org_id: request.org_id,
      caller_id: request.caller_id,
      title: request.title,
      description: request.description
    };

    if (request.service_id) fields.service_id = request.service_id;
    if (request.priority) fields.priority = request.priority;

    const result = await this.makeRequest<{
      objects: Record<string, { fields: Record<string, unknown> }> | null;
    }>('core/create', {
      class: 'UserRequest',
      comment: 'Created via Solvia iTop Outlook Add-in',
      fields,
      output_fields: 'ref, friendlyname'
    });

    const created = this.parseObjects(result.objects, (key, objFields) => ({
      id: parseInt(key.split('::')[1]),
      ref: objFields.ref as string,
      friendlyname: objFields.friendlyname as string
    }));

    if (created.length === 0) {
      throw new Error('Failed to create ticket - no response from iTop');
    }

    // Step 2: Assign ticket via ev_assign stimulus (if team or agent selected)
    if (request.team_id || request.agent_id) {
      const assignFields: Record<string, unknown> = {};
      if (request.team_id) assignFields.team_id = request.team_id;
      if (request.agent_id) assignFields.agent_id = request.agent_id;

      await this.makeRequest('core/apply_stimulus', {
        class: 'UserRequest',
        key: `SELECT UserRequest WHERE id = ${created[0].id}`,
        comment: 'Assigned via Solvia iTop Outlook Add-in',
        stimulus: 'ev_assign',
        fields: assignFields,
        output_fields: 'id, friendlyname'
      });
    }

    return created[0];
  }

  /**
   * Add to ticket public log
   */
  static async addToPublicLog(ticketId: number, message: string): Promise<void> {
    await this.makeRequest('core/update', {
      class: 'UserRequest',
      key: ticketId,
      comment: 'Updated via Solvia iTop Outlook Add-in',
      fields: {
        public_log: {
          add_item: {
            message: message
          }
        }
      }
    });
  }
}
